import React, { Fragment, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import Rightbar from 'pages/main/cheques/Rightbar'
import ChainFilterBar, { useChainFilters, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	percent, decimal, columnStats, heatStyle, intensityStyle, chainColor,
	ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

const ORDER_TYPE_KEYS = { 1: 'hall', 2: 'takeaway', 3: 'delivery' }

function ServiceRating() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [optionMode, setOptionMode] = useState('share')
	const [collapsed, setCollapsed] = useState({})
	const [details, setDetails] = useState(null)
	const [cheque, setCheque] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)

	const filters = useChainFilters({ order_type_ids: [], source_ids: [] }, searchWithFilter)
	const { applied } = filters
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/service', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const questions = useMemo(() => data?.questions || [], [data])
	const totals = data?.totals || {}
	const branchStats = useMemo(() => ({
		score: columnStats(branches, 'score'),
		coverage: columnStats(branches, 'coverage'),
	}), [branches])

	const scored = branches.filter(item => item.score !== null)
	const best = scored.length > 0 ? [...scored].sort((a, b) => b.score - a.score || b.feedbacks - a.feedbacks)[0] : null
	const worst = scored.length > 1 ? [...scored].sort((a, b) => a.score - b.score || b.feedbacks - a.feedbacks)[0] : null
	// Порядковый номер вопроса — подпись ответа в расшифровке
	const questionIndex = useMemo(() => Object.fromEntries(questions.map((item, index) => [item.id, index + 1])), [questions])

	const typeLabels = {
		rate: t('chain_question_rate'),
		choice: t('chain_question_choice'),
		multi: t('chain_question_multi'),
		text: t('chain_question_text'),
	}

	function questionLabel(question) {
		const parts = [typeLabels[question.type]]
		if (question.order_type_id && ORDER_TYPE_KEYS[question.order_type_id]) parts.push(t(ORDER_TYPE_KEYS[question.order_type_id]))
		if (question.status !== 1) parts.push(t('chain_question_removed'))
		return parts.join(' · ')
	}

	// Доля варианта в филиале — от ответивших на вопрос в этом филиале
	function optionShare(option, question, posId) {
		const answers = Number(question.poses?.[posId] || 0)
		return answers > 0 ? Number(option.poses?.[posId] || 0) / answers * 100 : null
	}

	async function openDetails({ posId = null, question = null, option = null }) {
		const response = await GET(buildAnalyticsUrl('/chain/report/service-feedbacks', applied, {
			pos_id: posId,
			question_id: question && !option ? question.id : null,
			option_id: option ? option.id : null,
		}), {}, { loader: true })
		if (httpOk(response)) {
			const branch = branches.find(item => item.pos_id === posId)
			setDetails({
				...response.data,
				question,
				pos_name: branch ? branch.pos_name : t('chain_all_branches'),
				label: option ? `${question.name} — ${option.value}` : question ? question.name : t('chain_all_feedbacks'),
			})
		}
	}

	async function openCheque(row) {
		if (!row.cheque_id) return
		const response = await GET(`/cheques/${row.cheque_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setCheque({ ...response.data.data, showSidebar: true })
			setShowRightBar(true)
		}
	}

	function feedbackColumns() {
		return [
			{ label: t('date'), type: 'text', width: 17 },
			{ label: t('chain_branch'), type: 'text', width: 26 },
			{ label: t('cheque'), type: 'text', width: 10 },
			{ label: t('amount'), type: 'money' },
			{ label: t('client'), type: 'text', width: 20 },
			{ label: t('phone'), type: 'text', width: 15 },
			{ label: t('cashier'), type: 'text', width: 18 },
			{ label: t('waiter'), type: 'text', width: 18 },
			{ label: t('courier'), type: 'text', width: 18 },
			...questions.map(question => ({ label: `${questionIndex[question.id]}. ${question.name}`, type: 'text', width: question.type === 'text' ? 40 : 24 })),
		]
	}

	function feedbackRow(row) {
		return [
			formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), row.pos_name, row.cheque_number ? `№${row.cheque_number}` : '',
			row.total_amount_service, row.client_name || '', row.client_phone || '', row.cashier_name || '', row.waiter_name || '', row.courier_name || '',
			...questions.map(question => row.answers?.[question.id] || ''),
		]
	}

	async function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		// Анкеты целиком — для второго листа (и вместо старой серверной выгрузки)
		const response = await GET(buildAnalyticsUrl('/chain/report/service-feedbacks', applied), {}, { loader: true })
		const feedbacks = httpOk(response) ? response.data.rows : []

		const matrixRows = []
		questions.forEach(question => {
			matrixRows.push([
				`${questionIndex[question.id]}. ${question.name}`,
				typeLabels[question.type],
				...branches.map(branch => question.numeric ? question.avg_by_pos?.[branch.pos_id] ?? '' : Number(question.poses?.[branch.pos_id] || 0)),
				question.numeric ? question.avg : question.answers,
				question.answers,
				'',
			])
			question.options.forEach(option => {
				matrixRows.push([
					`      ${option.value}`,
					'',
					...branches.map(branch => Number(option.poses?.[branch.pos_id] || 0)),
					option.total,
					'',
					question.answers > 0 ? option.total / question.answers * 100 : '',
				])
			})
		})

		downloadExcel(`${t('chain_service_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_questions_by_branches'),
				title: t('chain_service_title'),
				subtitle: `${period} · ${t('chain_service_excel_hint')}`,
				columns: [
					{ label: t('question'), type: 'text', width: 50 },
					{ label: t('chain_question_type'), type: 'text', width: 16 },
					...branches.map(branch => ({ label: branch.pos_name, type: 'number', width: 14 })),
					{ label: t('total'), type: 'number' },
					{ label: t('chain_answers'), type: 'int', width: 10 },
					{ label: t('share'), type: 'percent', width: 9 },
				],
				rows: matrixRows,
			},
			{
				name: t('chain_branches'),
				title: `${t('chain_service_title')} — ${t('chain_branches')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('chain_feedbacks'), type: 'int', width: 10 },
					{ label: t('cheques'), type: 'int', width: 10 },
					{ label: t('chain_coverage'), type: 'percent', width: 10 },
					{ label: t('chain_service_score'), type: 'number', width: 12 },
				],
				rows: [...branches].sort((a, b) => (b.score ?? -1) - (a.score ?? -1)).map(item => [item.pos_name, item.feedbacks, item.cheques, item.coverage, item.score]),
				footer: [t('total'), totals.feedbacks, totals.cheques, totals.coverage, totals.score],
			},
			{
				name: t('chain_feedbacks'),
				title: `${t('chain_feedbacks')}${response?.data?.limited ? ` — ${t('chain_details_limited')}` : ''}`,
				subtitle: period,
				columns: feedbackColumns(),
				rows: feedbacks.map(feedbackRow),
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_service_title')} — ${details.pos_name}`, [{
			name: t('chain_feedbacks'),
			title: `${details.label} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: feedbackColumns(),
			rows: details.rows.map(feedbackRow),
		}])
	}

	const allCollapsed = questions.length > 0 && questions.every(question => collapsed[question.id] || question.options.length === 0)
	function toggleAll() {
		setCollapsed(allCollapsed ? {} : Object.fromEntries(questions.map(question => [question.id, true])))
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_service_title')}
				icon="uil-clipboard-notes"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} />
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_feedbacks')} icon="uil-clipboard-notes" color="#5b73e8"
						value={formatMoney(totals.feedbacks || 0)}
						sub={<>{t('chain_coverage')} <b>{percent(totals.coverage, 2)}</b> {t('chain_of_cheques')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_service_score')} icon="uil-star" color="#f59f00" title2={t('chain_service_score_hint')}
						value={totals.score !== null && totals.score !== undefined ? decimal(totals.score) : '—'}
						sub={<>{formatMoney(totals.score_answers || 0)} {t('chain_answers_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_best_branch')} icon="uil-trophy" color="#12b886"
						value={best ? best.pos_name : '—'}
						sub={best ? <>{decimal(best.score)} ★ · {formatMoney(best.feedbacks)} {t('chain_feedbacks_lower')}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_worst_branch')} icon="uil-arrow-down-right" color="#f06548"
						value={worst ? worst.pos_name : '—'}
						sub={worst ? <>{decimal(worst.score)} ★ · {formatMoney(worst.feedbacks)} {t('chain_feedbacks_lower')}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_questions_by_branches')}</h5>
							<span className="chain-muted">{t('chain_options_show')}</span>
							<FilterSegment
								options={[{ id: 'share', label: t('chain_mode_share') }, { id: 'count', label: t('chain_mode_count') }]}
								value={optionMode}
								onChange={setOptionMode}
							/>
							<button type="button" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '10px' }} onClick={toggleAll}>
								<i className={`uil ${allCollapsed ? 'uil-angle-down' : 'uil-angle-up'} me-1`}></i>
								{allCollapsed ? t('chain_expand_all') : t('chain_collapse_all')}
							</button>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<th className="chain-sticky-2" style={{ minWidth: '320px' }}>{t('question')}</th>
									{branches.map((branch, index) => (
										<th key={branch.pos_id} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</th>
									))}
									<th className="text-end chain-total-col">{t('total')}</th>
								</tr>
							</thead>
							<tbody>
								{questions.length === 0 && <ChainEmpty colSpan={branches.length + 3} text={t('chain_service_empty')} />}
								{questions.map((question, rowIndex) => {
									const isOpen = !collapsed[question.id] && question.options.length > 0
									// Оценочный вопрос — средний балл по филиалу, остальные — число ответов
									const values = branches.map(branch => question.numeric
										? question.avg_by_pos?.[branch.pos_id] ?? null
										: Number(question.poses?.[branch.pos_id] || 0))
									const rowStats = columnStats(values.map(value => ({ value })), 'value')
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									return (
										<Fragment key={question.id}>
											<tr>
												<td className="chain-index chain-sticky">{rowIndex + 1}</td>
												<td className="chain-name chain-sticky-2" style={{ whiteSpace: 'normal' }}>
													<div className="d-flex align-items-start gap-1">
														{question.options.length > 0 && (
															<i className={`uil ${isOpen ? 'uil-angle-down' : 'uil-angle-right'} chain-click`}
																style={{ fontSize: '16px', lineHeight: 1.2 }}
																onClick={() => setCollapsed({ ...collapsed, [question.id]: isOpen })}></i>
														)}
														<div className="chain-click" title={t('chain_open_details')} onClick={() => openDetails({ question })}>
															{question.name}
															<div className="chain-muted">{questionLabel(question)}</div>
														</div>
													</div>
												</td>
												{branches.map((branch, index) => {
													const value = values[index]
													const answers = Number(question.poses?.[branch.pos_id] || 0)
													if (answers === 0) return <td key={branch.pos_id} className="chain-num chain-muted">—</td>
													return (
														<td key={branch.pos_id}
															className="chain-num chain-click"
															style={question.numeric ? heatStyle(value, rowStats) : intensityStyle(value, rowMax, 'primary')}
															title={`${branch.pos_name}: ${formatMoney(answers)} ${t('chain_answers_lower')}`}
															onClick={() => openDetails({ posId: branch.pos_id, question })}>
															{question.numeric
																? <><b>{decimal(value)}</b><div className="chain-muted">{formatMoney(answers)}</div></>
																: <span>{formatMoney(answers)}</span>}
														</td>
													)
												})}
												<td className="chain-num chain-total-col">
													{question.numeric
														? <>{question.avg !== null ? decimal(question.avg) : '—'}<div className="chain-muted">{formatMoney(question.answers)}</div></>
														: formatMoney(question.answers)}
												</td>
											</tr>
											{isOpen && question.options.map(option => {
												const optionValues = branches.map(branch => optionMode === 'share'
													? optionShare(option, question, branch.pos_id)
													: Number(option.poses?.[branch.pos_id] || 0))
												const optionMax = Math.max(0, ...optionValues.map(value => Number(value || 0)))
												const totalShare = question.answers > 0 ? option.total / question.answers * 100 : 0
												return (
													<tr key={`${question.id}-${option.id}`}>
														<td className="chain-index chain-sticky"></td>
														<td className="chain-sticky-2 chain-click" style={{ paddingLeft: '34px', whiteSpace: 'normal' }}
															title={t('chain_open_details')}
															onClick={() => option.total > 0 && openDetails({ question, option })}>
															{option.value}
															{option.status !== 1 && <span className="chain-muted"> · {t('chain_question_removed')}</span>}
														</td>
														{branches.map((branch, index) => {
															const value = optionValues[index]
															const quantity = Number(option.poses?.[branch.pos_id] || 0)
															return (
																<td key={branch.pos_id}
																	className={`chain-num ${quantity > 0 ? 'chain-click' : ''}`}
																	style={quantity > 0 ? intensityStyle(value, optionMax, 'success') : undefined}
																	title={quantity > 0 ? `${branch.pos_name}: ${formatMoney(quantity)} · ${percent(optionShare(option, question, branch.pos_id))}` : undefined}
																	onClick={() => quantity > 0 && openDetails({ posId: branch.pos_id, question, option })}>
																	{quantity > 0
																		? <span>{optionMode === 'share' ? percent(value, 0) : formatMoney(value)}</span>
																		: <span className="chain-muted">—</span>}
																</td>
															)
														})}
														<td className="chain-num chain-total-col">
															{optionMode === 'share' ? percent(totalShare, 0) : formatMoney(option.total)}
														</td>
													</tr>
												)
											})}
										</Fragment>
									)
								})}
							</tbody>
							{questions.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_feedbacks')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className={`chain-num ${branch.feedbacks > 0 ? 'chain-click' : ''}`}
												onClick={() => branch.feedbacks > 0 && openDetails({ posId: branch.pos_id })}>
												{formatMoney(branch.feedbacks)}
											</td>
										))}
										<td className="chain-num chain-total-col">{formatMoney(totals.feedbacks)}</td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_service_score_hint')}>{t('chain_service_score')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={heatStyle(branch.score, branchStats.score)}>
												{branch.score !== null ? decimal(branch.score) : '—'}
											</td>
										))}
										<td className="chain-num chain-total-col">{totals.score !== null && totals.score !== undefined ? decimal(totals.score) : '—'}</td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_coverage_hint')}>{t('chain_coverage')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={heatStyle(branch.coverage, branchStats.coverage)}>
												{percent(branch.coverage, 2)}
											</td>
										))}
										<td className="chain-num chain-total-col">{percent(totals.coverage, 2)}</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_service_matrix_hint')}
					</div>
				</div>
			</div>

			{scored.length > 0 && <ScoreChart branches={branches} />}

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						<div style={{ whiteSpace: 'normal' }}>{details?.label} — {details?.pos_name}</div>
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_feedbacks_lower')}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>{t('date')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('cheque')}</th>
								<th>{t('client')}</th>
								<th>{t('chain_staff')}</th>
								{details?.question && <th style={{ minWidth: '160px' }}>{t('chain_answer')}</th>}
								<th style={{ minWidth: '280px' }}>{t('chain_all_answers')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={details?.question ? 7 : 6} />}
							{(details?.rows || []).map(row => (
								<tr key={row.id}>
									<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
									<td>{row.pos_name}</td>
									<td className={`text-nowrap ${row.cheque_id ? 'chain-click' : ''}`} onClick={() => openCheque(row)}
										title={row.cheque_id ? t('chain_open_cheque') : t('chain_cheque_not_found')}>
										{row.cheque_id
											? <span className="text-primary">№{row.cheque_number}<div className="chain-muted">{formatMoney(Math.round(row.total_amount_service || 0))}</div></span>
											: <span className="chain-muted">—</span>}
									</td>
									<td className="text-nowrap">
										{row.client_name || '—'}
										{row.client_phone && <div className="chain-muted">{row.client_phone}</div>}
									</td>
									<td className="text-nowrap">
										{row.waiter_name && <div>{t('waiter')}: {row.waiter_name}</div>}
										{row.courier_name && <div>{t('courier')}: {row.courier_name}</div>}
										{row.cashier_name && <div className="chain-muted">{t('cashier')}: {row.cashier_name}</div>}
										{!row.waiter_name && !row.courier_name && !row.cashier_name && '—'}
									</td>
									{details?.question && <td style={{ whiteSpace: 'normal' }}><b>{row.answers?.[details.question.id] || '—'}</b></td>}
									<td style={{ whiteSpace: 'normal' }}>
										{questions.filter(question => row.answers?.[question.id]).map(question => (
											<div key={question.id} title={question.name}>
												<span className="chain-muted">{questionIndex[question.id]}.</span> {row.answers[question.id]}
											</div>
										))}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={cheque} />
		</>
	)
}

// Индекс сервиса филиалов — кто выше, кто ниже средней сети
function ScoreChart({ branches }) {
	const { t } = useTranslation()
	const rows = branches.filter(item => item.score !== null).sort((a, b) => b.score - a.score)
	const series = [{ name: t('chain_service_score'), data: rows.map(item => item.score) }]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%', distributed: true } },
		colors: rows.map(item => chainColor(branches.indexOf(item))),
		dataLabels: { enabled: true, formatter: value => Number(value).toFixed(2), style: { fontSize: '11px' } },
		legend: { show: false },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(item => item.pos_name), min: 0, max: 5, tickAmount: 5 },
		tooltip: { y: { formatter: value => Number(value).toFixed(2) } },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_service_score_by_branches')}</h5></div>
				<ReactApexChart type="bar" height={Math.max(220, rows.length * 38 + 70)} series={series} options={options} />
			</div>
		</div>
	)
}

export default ServiceRating
